import {
  ArrowRight,
  Combine,
  Database,
  Factory,
  Globe,
  LineChart,
  Settings,
  Shield,
  Users,
} from "lucide-react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  downAndUp,
  scrollVariantsLeft,
  scrollVariantsRight,
  upAndDown,
} from "../../../constants/TextAnimation";

const weOffer = [
  {
    title: "Business Immigration",
    description:
      "Specialized services for smooth immigration across the globe.,",
    icon: <LineChart className="h-5 w-5" />,
  },
  {
    title: "Advisory Services",
    description:
      "Comprehensive business advisory for growth, loans, staffing, and mergers and acquisitions.",
    icon: <Database className="h-5 w-5" />,
  },
  {
    title: "CFO Services",
    description:
      "Expert financial management and CFO guidance tailored for your business growth.",
    icon: <Shield className="h-5 w-5" />,
  },
  {
    title: "Global Business Setup",
    description:
      "Navigating international markets with seamless business setup solutions.",
    icon: <Globe className="h-5 w-5" />,
  },
  {
    title: "Business Brokerage",
    description:
      "Expert facilitation for seamless sale and purchase transaction of businesses.",
    icon: <Factory className="h-5 w-5" />,
  },
  {
    title: "Mergers and Acquisitions",
    description:
      "Assisting businesses with seamless mergers and acquisitions, from due diligence to integration, ensuring strategic growth and value creation.",
    icon: <Combine className="h-5 w-5" />,
  },
];

const WhatWeDo = () => {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0.0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        delay: 0.3,
        duration: 0.8,
        ease: "easeInOut",
      }}
    >
      {/* hero */}
      <section className="py-8 md:py-16 lg:pb-24 lg:pt-20">
        <div className="relative pt-16 pb-32 flex content-center items-center justify-center">
          <div
            className="absolute top-0 w-full h-full bg-center bg-cover bg-fixed"
            style={{
              backgroundImage: `url("/images/solutions/8.webp")`,
            }}
          >
            <span className="w-full h-full absolute opacity-60 bg-black"></span>
          </div>

          <div className="container relative mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={downAndUp}
              viewport={{ amount: 0.3 }}
              className="items-center flex flex-wrap"
            >
              <div className="w-full lg:w-6/12 px-4 ml-auto mr-auto text-center mt-24">
                <div className="pr-0 md:pt-12 ">
                  <h1 className="text-white font-bold text-4xl md:text-5xl leading-tight">
                    Tailored{" "}
                    <span className="gradient-text">business solutions</span>
                  </h1>
                  <p className="mt-4 text-lg text-gray-300">
                    Custom strategies to drive growth, efficiency, and long-term
                    success.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="pb-10 md:pb-16 lg:pb-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={upAndDown}
            viewport={{ amount: 0.3 }}
            className="max-w-3xl mx-auto mb-12 text-center"
          >
            <h3 className="text-gold font-semibold text-sm uppercase tracking-wide mb-1">
              OVERVIEW
            </h3>
            <h2 className="text-3xl font-bold sm:text-4xl mb-4">
              Technology Solutions for Every Business Need
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              We offer end-to-end technology services designed to help
              businesses innovate, optimize, and grow.
            </p>
          </motion.div>

          <div className="flex flex-wrap items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsLeft}
              viewport={{ amount: 0.3 }}
              className="w-full md:w-5/12 px-4 mr-auto ml-auto mb-8 md:mb-0"
            >
              <div className="text-gray-600 p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-6 shadow-lg rounded-full bg-gold/20">
                <Settings className="w-8 h-8 text-gold" />
              </div>
              <h3 className="text-3xl mb-2 font-semibold leading-normal">
                Tailor-Made Solutions
              </h3>
              <p className="text-lg font-light leading-relaxed mt-4 mb-4 text-gray-400">
                Our services are customized to meet your unique business
                requirements. We don&apos;t believe in one-size-fits-all
                approaches – instead, we carefully analyze your needs and
                develop solutions that address your specific challenges.
              </p>
              <p className="text-lg font-light leading-relaxed mt-0 mb-4 text-gray-400">
                From strategic consulting to implementation and ongoing support,
                we partner with you at every stage of your digital
                transformation journey.
              </p>
              <Link to="/solutions/contact">
                <button className="bg-gold text-slate-900 font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150">
                  Start Your Transformation
                </button>
              </Link>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsRight}
              viewport={{ amount: 0.3 }}
              className="w-full md:w-6/12 px-4 mr-auto ml-auto"
            >
              <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-slate-900/60">
                <img
                  alt="Technical professionals in a meeting"
                  src="/images/solutions/7.webp"
                  className="w-full align-middle rounded-t-lg"
                />
                <blockquote className="relative p-8 mb-4">
                  <h4 className="text-xl font-bold text-white">Our Approach</h4>
                  <p className="text-md font-light mt-2 text-white">
                    We combine deep technical expertise with business acumen to
                    deliver solutions that drive measurable results. Our
                    collaborative approach ensures that we understand your
                    business objectives and align our technology solutions
                    accordingly.
                  </p>
                  <div className="mt-6 flex items-center">
                    <div className="h-12 w-12 bg-gold/10 rounded-full flex justify-center items-center">
                      <Users className="h-6 w-6 text-gold" />
                    </div>
                    <div className="ml-4">
                      <h6 className="text-white text-xl">Client-Centric</h6>
                      <p className="text-white text-sm opacity-80">
                        Your success is our priority
                      </p>
                    </div>
                  </div>
                </blockquote>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="pb-10 md:pb-16 lg:pb-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={scrollVariantsLeft}
            viewport={{ amount: 0.3 }}
            className="max-w-3xl mx-auto mb-12 text-center"
          >
            <h2 className="text-3xl font-bold md:text-5xl mb-4">
              What we <span className="gradient-text">offer</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {weOffer.map((item, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                variants={scrollVariantsRight}
                viewport={{ amount: 0.3 }}
                className="bg-slate-900/50 rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group"
              >
                <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center text-gold mb-3">
                  {item.icon ? (
                    <span className="h-5 w-5">{item.icon}</span>
                  ) : (
                    <Settings className="h-5 w-5" />
                  )}
                </div>
                <h3 className="text-xl font-bold mb-1">{item.title}</h3>
                <p className="text-gray-600 mb-1">{item.description}</p>
                {/* <Link
                to=""
                className="inline-flex items-center text-gold font-medium hover:text-gold/80 transition-colors duration-200"
              >
                Learn more
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link> */}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-10 md:pb-16 lg:pb-32">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsLeft}
              viewport={{ amount: 0.3 }}
              className="w-full md:w-5/12 px-4 mr-auto ml-auto"
            >
              <div className="text-gold p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-6 shadow-lg rounded-full bg-gold/10">
                <LineChart className="w-8 h-8" />
              </div>
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-gradient-to-b from-secondary to-gold rounded-sm" />
                <h3 className="text-3xl font-semibold leading-normal">
                  Business Intelligence
                </h3>
              </div>

              <div className="mt-6">
                <div className="mt-6">
                  <h4 className="text-xl font-semibold mb-3">
                    Strategic Insights
                  </h4>
                  <ul className="list-disc pl-5 space-y-2 text-gray-400">
                    <li>
                      Unlocking potential through visionary strategy and
                      planning
                    </li>
                  </ul>
                </div>
                <div className="mt-6">
                  <h4 className="text-xl font-semibold mb-3">
                    Operational Support
                  </h4>
                  <ul className="list-disc pl-5 space-y-2 text-gray-400">
                    <li>
                      Ensuring operational excellence with robust support
                      systems
                    </li>
                  </ul>
                </div>
                <div className="mt-6">
                  <h4 className="text-xl font-semibold mb-3">
                    Growth Initiatives
                  </h4>
                  <ul className="list-disc pl-5 space-y-2 text-gray-400">
                    <li>
                      Driving business expansion through targeted growth
                      strategies
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsRight}
              viewport={{ amount: 0.3 }}
              className="w-full md:w-6/12 px-4 mr-auto ml-auto mt-8 md:mt-0"
            >
              <img
                alt="Business Intelligence Dashboard"
                src="/images/solutions/3.webp"
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="pb-10 md:pb-16 lg:pb-32">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsRight}
              viewport={{ amount: 0.3 }}
              className="w-full md:w-6/12 px-4 mr-auto ml-auto mb-8 md:mb-0"
            >
              <img
                alt="Cloud Computing Infrastructure"
                src="/images/solutions/2.jpg"
                className="w-full rounded-lg shadow-lg"
              />
              <div className="text-cente mt-5">
                <button
                  className="bg-gradient font-medium px-6 py-2 border rounded-lg border-slate-400"
                  onClick={() => navigate("/solutions/contact")}
                >
                  Get Started Now
                </button>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsLeft}
              viewport={{ amount: 0.3 }}
              className="w-full md:w-5/12 px-4 mr-auto ml-auto"
            >
              <div className="text-gold p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-6 shadow-lg rounded-full bg-gold/10">
                <Database className="w-8 h-8" />
              </div>
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-gradient-to-b from-secondary to-gold rounded-sm" />
                <h3 className="text-3xl font-semibold leading-normal">
                  Our strategic approach
                </h3>
              </div>

              <div className="mt-6">
                <h4 className="text-xl font-semibold mb-3">Initial Meeting</h4>
                <ul className="list-disc pl-5 space-y-2 text-gray-400">
                  <li>
                    Identify client objectives to customize service offerings.
                  </li>
                </ul>
              </div>
              <div className="mt-6">
                <h4 className="text-xl font-semibold mb-3">
                  Ongoing Assessment
                </h4>
                <ul className="list-disc pl-5 space-y-2 text-gray-400">
                  <li>
                    Collect required information and conduct continuous
                    evaluations, enabling clients to concentrate on their core
                    operations.
                  </li>
                </ul>
              </div>
              <div className="mt-6">
                <h4 className="text-xl font-semibold mb-3">
                  Solution & Results
                </h4>
                <ul className="list-disc pl-5 space-y-2 text-gray-400">
                  <li>
                    Describe how you integrate various elements to produce
                    effective and measurable outcomes for your clients.
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default WhatWeDo;
